import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
/**
 * The org's background photography behind the pre-meeting (prejoin) screen.
 *
 * Reads the same themed assets every other Kamo surface does — kamo-login,
 * kamo-register, the marketing site and this repo's static welcome page:
 * `<themePath>/json/config.json` supplies `backgroundImageCount`, and the frames
 * are `<themePath>/img/bg/{1..N}.webp`.
 *
 * `backgroundOrder` in that same file is the org-wide setting made in
 * kamo-internal under Settings -> Essential -> Behavior & Labeling -> Background
 * Images. 'shuffle' re-randomises on every advance; anything else — including an
 * absent key, which is what every org has until it chooses — plays in sequence.
 *
 * Renders nothing at all when the org has no backgrounds, so the flat
 * primary-colour prejoin background shows through exactly as it did before.
 */
const CYCLE_MS = 8000;
/**
 * The index to show after the current one. Shuffle excludes the frame already on
 * screen — a random draw that can return it reads as a stalled slideshow.
 *
 * @param {number} current - The index currently on screen.
 * @param {number} count - How many frames are in the slideshow.
 * @param {BackgroundOrder} order - The org's configured play order.
 * @returns {number} The index to show next.
 */
function nextBackgroundIndex(current, count, order) {
    if (count <= 1) {
        return 0;
    }
    if (order !== 'shuffle') {
        return (current + 1) % count;
    }
    const draw = Math.floor(Math.random() * (count - 1));
    const safeCurrent = current >= 0 && current < count ? current : 0;
    return draw >= safeCurrent ? draw + 1 : draw;
}
const useStyles = makeStyles()(() => {
    return {
        layer: {
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            // The bottom layer of the pre-meeting screen: behind the preview
            // column (lifted to z-index 1) and the join column (252), both of
            // which tint it at 50% rather than hide it.
            zIndex: 0,
            pointerEvents: 'none'
        },
        frame: {
            position: 'absolute',
            inset: 0,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0,
            transition: 'opacity 1.2s ease-in-out'
        },
        frameActive: {
            opacity: 1
        },
        frameStatic: {
            transition: 'none'
        }
    };
});
const ThemedBackgroundSlideshow = ({ onActiveChange }) => {
    const { classes, cx } = useStyles();
    const [images, setImages] = useState([]);
    const [index, setIndex] = useState(0);
    const [order, setOrder] = useState('sequential');
    const [reduceMotion, setReduceMotion] = useState(false);
    // Read at fire time so a config change cannot restart the current dwell.
    const orderRef = useRef('sequential');
    orderRef.current = order;
    useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const apply = () => setReduceMotion(Boolean(mq?.matches));
        apply();
        mq?.addEventListener?.('change', apply);
        return () => mq?.removeEventListener?.('change', apply);
    }, []);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            // branding/overrides.js publishes this global and fires
            // 'kamo-branding-loaded'; it may or may not have landed before mount.
            const themePath = window.__KAMO_MEET_BRANDING?.themePath;
            if (!themePath) {
                return;
            }
            try {
                const res = await fetch(`${themePath}/json/config.json`);
                if (!res.ok) {
                    return;
                }
                const cfg = await res.json();
                const rawCount = cfg?.backgroundImageCount;
                const count = typeof rawCount === 'number' && rawCount >= 1 ? rawCount : 0;
                if (cancelled || count < 1) {
                    return;
                }
                setOrder(cfg?.backgroundOrder === 'shuffle' ? 'shuffle' : 'sequential');
                const urls = Array.from({ length: count }, (_, i) => `${themePath}/img/bg/${i + 1}.webp`);
                // Preload and drop anything that 404s, so the crossfade only ever
                // cycles frames that exist and the first swap is instant.
                const settled = await Promise.all(urls.map(url => new Promise(resolve => {
                    const img = new window.Image();
                    img.onload = () => resolve(url);
                    img.onerror = () => resolve(null);
                    img.src = url;
                })));
                if (!cancelled) {
                    setImages(settled.filter((url) => url !== null));
                }
            }
            catch (e) {
                // No themed backgrounds — the flat prejoin background stands.
            }
        };
        void load();
        window.addEventListener('kamo-branding-loaded', load);
        return () => {
            cancelled = true;
            window.removeEventListener('kamo-branding-loaded', load);
        };
    }, []);
    useEffect(() => {
        onActiveChange?.(images.length > 0);
    }, [images.length, onActiveChange]);
    useEffect(() => {
        if (reduceMotion || images.length < 2) {
            return;
        }
        const id = window.setInterval(() => setIndex(i => nextBackgroundIndex(i, images.length, orderRef.current)), CYCLE_MS);
        return () => window.clearInterval(id);
    }, [images, reduceMotion]);
    if (images.length === 0) {
        return null;
    }
    return (React.createElement("div", { "aria-hidden": true, className: classes.layer }, images.map((src, i) => (React.createElement("div", { className: cx(classes.frame, i === index && classes.frameActive, reduceMotion && classes.frameStatic), key: src, style: { backgroundImage: `url("${src}")` } })))));
};
export default ThemedBackgroundSlideshow;
